import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Database, Link, FileText } from 'lucide-react';
import type { EvidenceItem } from '../../types/investigator';
import { getEvidenceCategoryLabel, getEvidenceSourceLabel } from '../../utils/evidenceDisplay';
import { humanizeInvestigationDateTimeText } from '../../utils/humanizeInvestigationDateTimeText';
import { PROVENANCE_BADGE_CLASSES } from '../../config/colors';
import { formatUkTime } from '../../lib/calc';

// Tier badge colours come from the shared colors module (ticket 15) —
// previously this panel painted "inferred" with the confirmed green, so one
// claim read "Confirmed" here and "Inferred" on the timeline.

// User-facing labels for the provenance badge — keep in sync with the same
// mapping in TimelinePanel.tsx so the Evidence and Timeline panels speak the
// same language to fleet managers ('Confirmed' / 'Projected' read more
// naturally than the internal 'observed' / 'inferred' codes).
const PROVENANCE_LABEL: Record<string, string> = {
  observed: 'Confirmed',
  declared: 'Declared',
  // "Inferred" not "Projected" — these had drifted apart, so one claim read
  // "Confirmed" on the timeline and "Projected" here. "Projected" also reads
  // as a forecast about the future rather than a deduction about the past.
  inferred: 'Inferred',
};

interface EvidenceCardProps {
  item: EvidenceItem;
  index: number;
  focusedEvidenceIndex: number | null;
  evidenceFocusNonce: number;
}

function EvidenceCard({ item, index, focusedEvidenceIndex, evidenceFocusNonce }: EvidenceCardProps) {
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const ts = new Date(item.occurred_at);
  const displayNum = index + 1;
  const isHighlighted = focusedEvidenceIndex === index;
  const explanationText = useMemo(
    () => humanizeInvestigationDateTimeText(item.explanation || ''),
    [item.explanation],
  );

  useEffect(() => {
    if (focusedEvidenceIndex !== index) return;
    setExpanded(true);
    const id = window.requestAnimationFrame(() => {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [focusedEvidenceIndex, evidenceFocusNonce, index]);

  return (
    <div
      ref={rootRef}
      className={`rounded-lg transition-shadow duration-300 ${
        isHighlighted ? 'ring-2 ring-ring/55 shadow-glow-blue-xl-faint' : ''
      }`}
    >
      <div className="bg-card/60 rounded-lg border border-border/50 overflow-hidden">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-start gap-2 sm:gap-3 p-3 text-left hover:bg-card transition-colors"
        >
          <span
            className="mt-0.5 flex h-7 min-w-7 shrink-0 items-center justify-center rounded-md bg-secondary/95 px-1.5 text-xs font-semibold tabular-nums text-destructive ring-1 ring-inset ring-accent/70"
            aria-label={`Evidence ${displayNum}`}
          >
            {displayNum}
          </span>
          {expanded ? (
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-medium text-foreground">{getEvidenceCategoryLabel(item)}</span>
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${PROVENANCE_BADGE_CLASSES[item.provenance_kind] ?? 'bg-muted text-muted-foreground'}`}
              >
                {PROVENANCE_LABEL[item.provenance_kind] || item.provenance_kind}
              </span>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-line">{explanationText}</p>
          </div>
          <div className="text-xs text-muted-foreground whitespace-nowrap mt-0.5">
            {formatUkTime(ts, {}, '')}
          </div>
        </button>

        {expanded && (
          <div className="px-3 pb-3 pt-0 pl-16 space-y-2 border-t border-border/50">
            <p className="text-xs text-foreground leading-relaxed mt-2 whitespace-pre-line">{explanationText}</p>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Database className="w-3 h-3" />
                Source:{' '}
                <span className="text-foreground">
                  {getEvidenceSourceLabel(item.source_system) ?? '—'}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <FileText className="w-3 h-3" />
                Weight: <span className="text-foreground">{Math.round(item.confidence_weight * 100)}%</span>
              </div>
            </div>

            {item.raw_payload_ref && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <FileText className="w-3 h-3" />
                Payload: <span className="font-mono text-muted-foreground">{item.raw_payload_ref}</span>
              </div>
            )}

            {item.canonical_entity_refs.length > 0 && (
              <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <Link className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <div className="flex flex-wrap gap-1">
                  {item.canonical_entity_refs.map((ref, i) => (
                    <span key={i} className="px-1.5 py-0.5 bg-secondary rounded font-mono text-muted-foreground">
                      {ref}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export interface EvidencePanelProps {
  evidence: EvidenceItem[];
  /** When true, show the loading helper line under the header (e.g. skeleton or in-panel fetch). */
  isLoading?: boolean;
  /** 0-based index; citations in answer text use [n] where n matches evidence order */
  focusedEvidenceIndex?: number | null;
  /** Bump when re-focusing the same index so scroll runs again */
  evidenceFocusNonce?: number;
}

export default function EvidencePanel({
  evidence,
  isLoading = false,
  focusedEvidenceIndex = null,
  evidenceFocusNonce = 0,
}: EvidencePanelProps) {
  return (
    <div className="h-full overflow-y-auto pr-1">
      <div className="mb-3">
        {isLoading ? (
          <p className="text-xs text-muted-foreground mt-0.5">
            Sources and refs will appear here once an investigation runs.
          </p>
        ) : null}
      </div>

      {evidence.length === 0 ? (
        <div className="flex items-center justify-center h-105 text-muted-foreground text-sm">
          No evidence collected
        </div>
      ) : (
        <div className="space-y-2">
          {evidence.map((item, i) => (
            <EvidenceCard
              key={item.evidence_id || i}
              item={item}
              index={i}
              focusedEvidenceIndex={focusedEvidenceIndex}
              evidenceFocusNonce={evidenceFocusNonce}
            />
          ))}
        </div>
      )}
    </div>
  );
}
